export type ScheduleWindow = {
  start_time: string;
  end_time: string;
  break_label?: string;
  /** Optional name for a work stretch between pauses (middle segment). */
  work_label?: string;
};

export type DayScheduleRow = {
  day_of_week: number;
  enabled: boolean;
  start_time: string;
  end_time: string;
  break_enabled: boolean;
  break_start: string;
  break_end: string;
  break_label: string;
  /** More than one pause. Times stay editable; turning the day off and on again replaces them with one pause. */
  preservedWindows: ScheduleWindow[] | null;
};

export type SchedulePayloadSlot = {
  day_of_week: number;
  start_time: string;
  end_time: string;
  break_label?: string;
  work_label?: string;
};

export type ScheduleRowError = "timesRequired" | "endAfterStart" | "breakInside" | "windowsOverlap";

const DEFAULT_START = "09:00";
const DEFAULT_END = "18:00";
const DEFAULT_BREAK_START = "12:00";
const DEFAULT_BREAK_END = "13:00";

export const normalizeTimeInput = (value: string): string => {
  if (!value) {
    return "";
  }

  return value.slice(0, 5);
};

const emptyRow = (dayOfWeek: number): DayScheduleRow => ({
  day_of_week: dayOfWeek,
  enabled: false,
  start_time: DEFAULT_START,
  end_time: DEFAULT_END,
  break_enabled: false,
  break_start: DEFAULT_BREAK_START,
  break_end: DEFAULT_BREAK_END,
  break_label: "",
  preservedWindows: null,
});

export const buildEmptyScheduleRows = (): DayScheduleRow[] =>
  Array.from({ length: 7 }, (_, dayOfWeek) => emptyRow(dayOfWeek));

const compareTime = (left: string, right: string): number =>
  normalizeTimeInput(left).localeCompare(normalizeTimeInput(right));

export const rowsFromApi = (
  slots: Array<{
    day_of_week: number;
    start_time: string;
    end_time: string;
    break_label?: string | null;
    work_label?: string | null;
  }>
): DayScheduleRow[] => {
  const rows = buildEmptyScheduleRows();
  const byDay = new Map<number, ScheduleWindow[]>();

  slots.forEach((slot) => {
    const windows = byDay.get(slot.day_of_week) ?? [];
    const label = slot.break_label?.trim() ?? "";
    const workLabel = slot.work_label?.trim() ?? "";
    windows.push({
      start_time: normalizeTimeInput(slot.start_time),
      end_time: normalizeTimeInput(slot.end_time),
      ...(label ? { break_label: label.slice(0, 40) } : {}),
      ...(workLabel ? { work_label: workLabel.slice(0, 40) } : {}),
    });
    byDay.set(slot.day_of_week, windows);
  });

  byDay.forEach((windows, dayOfWeek) => {
    const row = rows.find((item) => item.day_of_week === dayOfWeek);

    if (!row) {
      return;
    }

    const ordered = [...windows].sort((left, right) => compareTime(left.start_time, right.start_time));

    row.enabled = true;

    if (ordered.length === 1) {
      row.start_time = ordered[0].start_time;
      row.end_time = ordered[0].end_time;
      return;
    }

    if (ordered.length === 2 && ordered[1].start_time > ordered[0].end_time) {
      row.start_time = ordered[0].start_time;
      row.end_time = ordered[1].end_time;
      row.break_enabled = true;
      row.break_start = ordered[0].end_time;
      row.break_end = ordered[1].start_time;
      row.break_label = ordered[0].break_label ?? "";
      return;
    }

    if (ordered.length === 2 && ordered[1].start_time === ordered[0].end_time) {
      row.start_time = ordered[0].start_time;
      row.end_time = ordered[1].end_time;
      return;
    }

    row.preservedWindows = ordered;
    row.start_time = ordered[0].start_time;
    row.end_time = ordered[ordered.length - 1].end_time;
  });

  return rows;
};

const windowsOverlap = (windows: ScheduleWindow[]): boolean => {
  for (let index = 1; index < windows.length; index += 1) {
    if (windows[index].start_time < windows[index - 1].end_time) {
      return true;
    }
  }

  return false;
};

const pauseLabelOnGap = (
  row: DayScheduleRow,
  visit: (label: string) => void
): void => {
  if (!row.enabled) {
    return;
  }

  if (row.preservedWindows) {
    row.preservedWindows.forEach((window, index, windows) => {
      const next = windows[index + 1];

      if (next && next.start_time > window.end_time) {
        visit(window.break_label?.trim() ?? "");
      }
    });
    return;
  }

  if (row.break_enabled) {
    visit(row.break_label.trim());
  }
};

/** Names already on a pause that differ from the one about to be copied. */
export const breakNamesThatWouldChange = (rows: DayScheduleRow[], label: string): string[] => {
  const next = label.trim().slice(0, 40);

  if (next === "") {
    return [];
  }

  const found = new Set<string>();

  rows.forEach((row) => {
    pauseLabelOnGap(row, (current) => {
      if (current !== "" && current !== next) {
        found.add(current);
      }
    });
  });

  return Array.from(found);
};

/** Old pause names with the weekdays where they appear (for replace confirm). */
export const breakNamesWithDaysThatWouldChange = (
  rows: DayScheduleRow[],
  label: string
): Array<{ name: string; days: number[] }> => {
  const next = label.trim().slice(0, 40);

  if (next === "") {
    return [];
  }

  const byName = new Map<string, number[]>();

  rows.forEach((row) => {
    pauseLabelOnGap(row, (current) => {
      if (current === "" || current === next) {
        return;
      }

      const days = byName.get(current) ?? [];
      if (!days.includes(row.day_of_week)) {
        days.push(row.day_of_week);
      }
      byName.set(current, days);
    });
  });

  return Array.from(byName.entries()).map(([name, days]) => ({
    name,
    days: [...days].sort((a, b) => a - b),
  }));
};

/** Days that already have a pause and will receive a shared name. */
export const countDaysWithPause = (rows: DayScheduleRow[]): number =>
  rows.filter((row) => {
    if (!row.enabled) {
      return false;
    }

    if (row.preservedWindows) {
      return row.preservedWindows.some((window, index, windows) => {
        const next = windows[index + 1];

        return Boolean(next && next.start_time > window.end_time);
      });
    }

    return row.break_enabled;
  }).length;

/** True when every marked pause already uses this name (copy would be a no-op). */
export const allPausesAlreadyHaveLabel = (
  rows: DayScheduleRow[],
  label: string
): boolean => {
  const next = label.trim().slice(0, 40);

  if (next === "" || countDaysWithPause(rows) === 0) {
    return false;
  }

  let sawPause = false;
  let allMatch = true;

  rows.forEach((row) => {
    pauseLabelOnGap(row, (current) => {
      sawPause = true;
      if (current !== next) {
        allMatch = false;
      }
    });
  });

  return sawPause && allMatch;
};

/** Change one edge of a saved extra window and keep the day's outer hours in sync. */
export const withPreservedWindowTime = (
  row: DayScheduleRow,
  index: number,
  edge: "start_time" | "end_time",
  value: string
): DayScheduleRow => {
  if (!row.preservedWindows || !row.preservedWindows[index]) {
    return row;
  }

  const preservedWindows = row.preservedWindows.map((window, windowIndex) =>
    windowIndex === index ? { ...window, [edge]: normalizeTimeInput(value) } : window
  );

  return {
    ...row,
    preservedWindows,
    start_time: preservedWindows[0].start_time,
    end_time: preservedWindows[preservedWindows.length - 1].end_time,
  };
};

/** Optional name for a work stretch between pauses. */
export const withPreservedWorkLabel = (
  row: DayScheduleRow,
  index: number,
  label: string
): DayScheduleRow => {
  if (!row.preservedWindows || !row.preservedWindows[index]) {
    return row;
  }

  const trimmed = label.trim().slice(0, 40);

  return {
    ...row,
    preservedWindows: row.preservedWindows.map((window, windowIndex) => {
      if (windowIndex !== index) {
        return window;
      }

      if (trimmed === "") {
        const { work_label: _removed, ...rest } = window;
        return rest;
      }

      return { ...window, work_label: trimmed };
    }),
  };
};

export const validateScheduleRows = (rows: DayScheduleRow[]): ScheduleRowError | null => {
  for (const row of rows) {
    if (!row.enabled) {
      continue;
    }

    if (row.preservedWindows) {
      for (const window of row.preservedWindows) {
        if (!window.start_time || !window.end_time) {
          return "timesRequired";
        }

        if (window.end_time <= window.start_time) {
          return "endAfterStart";
        }
      }

      if (windowsOverlap(row.preservedWindows)) {
        return "windowsOverlap";
      }

      continue;
    }

    if (!row.start_time || !row.end_time) {
      return "timesRequired";
    }

    if (row.end_time <= row.start_time) {
      return "endAfterStart";
    }

    if (!row.break_enabled) {
      continue;
    }

    if (!row.break_start || !row.break_end) {
      return "timesRequired";
    }

    if (
      row.break_start <= row.start_time
      || row.break_end >= row.end_time
      || row.break_end <= row.break_start
    ) {
      return "breakInside";
    }
  }

  return null;
};

export const rowsToSchedules = (rows: DayScheduleRow[]): SchedulePayloadSlot[] => {
  const schedules: SchedulePayloadSlot[] = [];

  rows.forEach((row) => {
    if (!row.enabled) {
      return;
    }

    if (row.preservedWindows) {
      row.preservedWindows.forEach((window) => {
        const label = window.break_label?.trim().slice(0, 40) ?? "";
        const workLabel = window.work_label?.trim().slice(0, 40) ?? "";
        schedules.push({
          day_of_week: row.day_of_week,
          start_time: window.start_time,
          end_time: window.end_time,
          ...(label ? { break_label: label } : {}),
          ...(workLabel ? { work_label: workLabel } : {}),
        });
      });
      return;
    }

    if (row.break_enabled) {
      const label = row.break_label.trim().slice(0, 40);
      schedules.push({
        day_of_week: row.day_of_week,
        start_time: row.start_time,
        end_time: row.break_start,
        ...(label ? { break_label: label } : {}),
      });
      schedules.push({
        day_of_week: row.day_of_week,
        start_time: row.break_end,
        end_time: row.end_time,
      });
      return;
    }

    schedules.push({
      day_of_week: row.day_of_week,
      start_time: row.start_time,
      end_time: row.end_time,
    });
  });

  return schedules;
};

/** Copy one pause name onto every pause already marked in the week. */
export const applyBreakNameToAllDays = (
  rows: DayScheduleRow[],
  label: string
): DayScheduleRow[] => {
  const name = label.trim().slice(0, 40);

  if (name === "") {
    return rows;
  }

  return rows.map((row) => {
    if (row.preservedWindows) {
      return {
        ...row,
        preservedWindows: row.preservedWindows.map((window, index, windows) => {
          const next = windows[index + 1];

          if (!next || next.start_time <= window.end_time) {
            return window;
          }

          return { ...window, break_label: name };
        }),
      };
    }

    if (!row.enabled || !row.break_enabled) {
      return row;
    }

    return { ...row, break_label: name };
  });
};
